import { ChangeEvent, useState } from 'react';
import { useIntl } from 'react-intl';
import DialogTitle from '@rossum/components/dialog-title/DialogTitle';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@rossum/ui/material';
import { notify } from '../../toaster';
import { useDatasetUpload } from '../hooks/useDatasetUpload';
import { Dataset } from '../types';
import { UploadArea } from './UploadArea';

const maxFileSize = 52428800; // 50 MB

export const ReplaceDataDialog = ({
  open,
  onClose,
  table,
  hasRows,
}: {
  open: boolean;
  onClose: () => void;
  table: Dataset;
  // An empty dataset gets its first data instead of having it replaced.
  hasRows: boolean;
}) => {
  const intl = useIntl();
  const [file, setFile] = useState<File | null>(null);
  const { mutate: uploadData, isLoading: isReplacing } = useDatasetUpload();

  // The dialog stays mounted, so a closed one must not keep the last file.
  const handleClose = () => {
    setFile(null);
    onClose();
  };

  // Declared columns stay and the file is checked against them; inferred
  // ones are taken from the file again.
  const declared = table.schema_mode === 'validate';
  const descriptionId = hasRows
    ? declared
      ? 'features.datasets.data.replace.description.declared'
      : 'features.datasets.data.replace.description.inferred'
    : declared
      ? 'features.datasets.data.upload.description.declared'
      : 'features.datasets.data.upload.description.inferred';

  const actionLabel = intl.formatMessage({
    id: hasRows
      ? 'features.datasets.data.replace'
      : 'features.datasets.data.upload',
  });

  return (
    <Dialog
      open={open}
      component="form"
      onSubmit={e => {
        e.preventDefault();
        if (file) {
          uploadData(
            {
              file,
              datasetName: table.name,
              encoding: 'utf-8',
              dynamic: true,
            },
            {
              onSuccess: () => {
                notify.default({
                  title: intl.formatMessage({
                    id: 'features.datasets.notification.importStarted',
                  }),
                });
                handleClose();
              },
              onError: () => {
                notify.error({
                  title: intl.formatMessage({
                    id: 'features.datasets.notification.importFailed',
                  }),
                });
              },
            }
          );
        }
      }}
      onClose={handleClose}
    >
      <DialogTitle title={actionLabel} onClose={handleClose} />
      <DialogContent>
        <Stack pt={2} spacing={2}>
          <Typography variant="body1">
            {intl.formatMessage({ id: descriptionId })}
          </Typography>
          <UploadArea
            file={file}
            onUploadChange={(e: ChangeEvent<HTMLInputElement>) => {
              const selectedFile = e.target.files?.[0];
              if (selectedFile) {
                if (selectedFile.size > maxFileSize) {
                  // return setFileSizeError(true);
                }
                // setFileSizeError(false);
                return setFile(selectedFile);
              }
              return setFile(null);
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={!file || isReplacing}
        >
          {actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
